const MAIN_MAP_SLOT = "main";

export const MAP_POOL_SLOTS = {
  MAIN: MAIN_MAP_SLOT,
  ROUTE_DETAIL: "route-detail",
  ALERT_DETAIL: "alert-detail",
  GEO_DETAIL: "geo-detail",
};

export const DEFAULT_VECTOR_MAP_STYLES = [
  {
    featureType: "administrative",
    elementType: "geometry",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "landscape.man_made",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "landscape.natural",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "transit",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ visibility: "simplified" }],
  },
];

export const MAP_RESET_VIEW = {
  DEFAULT: {
    center: { lat: 24.458489, lng: -102.217231 },
    zoom: 5,
  },
  MEXICO_BOUNDS: {
    boundsPoints: [
      { lat: 32.534353, lng: -117.123783 },
      { lat: 21.137926, lng: -86.740844 },
      { lat: 14.534659, lng: -92.231633 },
    ],
  },
};

const slots = new Map();
let parkingNode;
const RESET_DELAYS_MS = [0, 80, 220, 450, 900, 1500, 2300];
const RESET_VISIBILITY_RETRY_MS = 140;
const RESET_VISIBILITY_RETRY_LIMIT = 8;

function ensureParkingNode() {
  if (parkingNode && document.body.contains(parkingNode)) {
    return parkingNode;
  }

  parkingNode = document.createElement("div");
  parkingNode.id = "vector-map-pool-parking";
  parkingNode.style.position = "fixed";
  parkingNode.style.left = "-10000px";
  parkingNode.style.top = "-10000px";
  parkingNode.style.width = "1px";
  parkingNode.style.height = "1px";
  parkingNode.style.opacity = "0";
  parkingNode.style.pointerEvents = "none";
  parkingNode.style.overflow = "hidden";
  document.body.appendChild(parkingNode);
  return parkingNode;
}

function ensureSlot(slotId) {
  if (!slots.has(slotId)) {
    slots.set(slotId, {
      map: null,
      container: null,
      mountNode: null,
      ownerId: null,
      cleanup: null,
      resetTimers: [],
      resetIdleListeners: [],
      resetSequence: 0,
      recreateOnRelease: false,
    });
  }

  return slots.get(slotId);
}

function ensureContainer(slot) {
  if (slot.container) {
    return slot.container;
  }

  slot.container = document.createElement("div");
  slot.container.className = "vector-map-pool-slot";
  slot.container.style.width = "100%";
  slot.container.style.height = "100%";
  slot.container.style.minHeight = "100%";
  return slot.container;
}

function clearScheduledResets(slot) {
  slot.resetSequence = (slot.resetSequence || 0) + 1;

  if (!Array.isArray(slot.resetTimers)) {
    slot.resetTimers = [];
  } else {
    for (let timerId of slot.resetTimers) {
      clearTimeout(timerId);
    }

    slot.resetTimers = [];
  }

  if (!Array.isArray(slot.resetIdleListeners)) {
    slot.resetIdleListeners = [];
    return;
  }

  for (let listener of slot.resetIdleListeners) {
    if (listener) {
      google.maps.event.removeListener(listener);
    }
  }

  slot.resetIdleListeners = [];
}

function buildBounds(boundsPoints) {
  let bounds = new google.maps.LatLngBounds();

  for (let point of boundsPoints) {
    bounds.extend(point);
  }

  return bounds;
}

function clearMapControls(map) {
  let positions = new Set();

  for (let key of Object.keys(google.maps.ControlPosition)) {
    let position = google.maps.ControlPosition[key];
    if (typeof position === "number") {
      positions.add(position);
    }
  }

  for (let position of positions) {
    map.controls[position].clear();
  }
}

function applyResetView(map, resetView) {
  if (!map || !resetView) {
    return;
  }

  if (Array.isArray(resetView.boundsPoints) && resetView.boundsPoints.length > 0) {
    map.fitBounds(buildBounds(resetView.boundsPoints));
    if (typeof resetView.zoom === "number") {
      map.setZoom(resetView.zoom);
    }
    return;
  }

  if (resetView.center) {
    map.setCenter(resetView.center);
  }

  if (typeof resetView.zoom === "number") {
    map.setZoom(resetView.zoom);
  }
}

function scheduleReset(slot, resetView) {
  if (!slot.map) {
    return;
  }

  clearScheduledResets(slot);
  let resetSequence = slot.resetSequence;

  let queueReset = (delay, visibilityRetry = 0) => {
    let timerId = setTimeout(() => {
      requestAnimationFrame(() => {
        runReset(visibilityRetry, resetSequence);
      });
    }, delay);
    slot.resetTimers.push(timerId);
  };

  let runReset = (visibilityRetry = 0, expectedSequence) => {
    if (expectedSequence !== slot.resetSequence) {
      return;
    }

    if (!slot.map || !slot.mountNode || !slot.mountNode.isConnected) {
      return;
    }

    let rect = slot.mountNode.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      if (visibilityRetry < RESET_VISIBILITY_RETRY_LIMIT) {
        queueReset(RESET_VISIBILITY_RETRY_MS, visibilityRetry + 1);
      }
      return;
    }

    google.maps.event.trigger(slot.map, "resize");
    applyResetView(slot.map, resetView);

    requestAnimationFrame(() => {
      if (expectedSequence !== slot.resetSequence) {
        return;
      }

      if (!slot.map || !slot.mountNode || !slot.mountNode.isConnected) {
        return;
      }
      google.maps.event.trigger(slot.map, "resize");
      applyResetView(slot.map, resetView);
    });

    let idleListener = google.maps.event.addListenerOnce(slot.map, "idle", () => {
      slot.resetIdleListeners = slot.resetIdleListeners.filter(
        (listener) => listener !== idleListener
      );

      if (expectedSequence !== slot.resetSequence) {
        return;
      }

      if (!slot.map || !slot.mountNode || !slot.mountNode.isConnected) {
        return;
      }

      let idleRect = slot.mountNode.getBoundingClientRect();
      if (idleRect.width === 0 || idleRect.height === 0) {
        return;
      }

      google.maps.event.trigger(slot.map, "resize");
      applyResetView(slot.map, resetView);
    });
    slot.resetIdleListeners.push(idleListener);

    let nestedTimerId = setTimeout(() => {
      if (expectedSequence !== slot.resetSequence) {
        return;
      }

      if (!slot.map || !slot.mountNode || !slot.mountNode.isConnected) {
        return;
      }
      google.maps.event.trigger(slot.map, "resize");
      applyResetView(slot.map, resetView);
    }, 80);
    slot.resetTimers.push(nestedTimerId);
  };

  for (let delay of RESET_DELAYS_MS) {
    queueReset(delay);
  }
}

export function createVectorMapOptions(overrides = {}) {
  return {
    center: MAP_RESET_VIEW.DEFAULT.center,
    zoom: MAP_RESET_VIEW.DEFAULT.zoom,
    styles: DEFAULT_VECTOR_MAP_STYLES,
    ...overrides,
  };
}

export function acquirePooledMap(slotId, ownerId, mountNode, config = {}) {
  if (!mountNode) {
    return null;
  }

  let slot = ensureSlot(slotId);
  let container = ensureContainer(slot);
  let mapOptions = config.mapOptions || createVectorMapOptions();
  let resetView = config.resetView || MAP_RESET_VIEW.DEFAULT;

  if (slot.ownerId && slot.ownerId !== ownerId) {
    releasePooledMap(slotId, slot.ownerId);
  }

  slot.ownerId = ownerId;
  slot.mountNode = mountNode;
  slot.cleanup =
    typeof config.cleanup === "function" ? config.cleanup : null;
  slot.recreateOnRelease = config.recreateOnRelease === true;

  if (container.parentNode !== mountNode) {
    mountNode.textContent = "";
    mountNode.appendChild(container);
  }

  if (!slot.map) {
    slot.map = new google.maps.Map(container, mapOptions);
  } else {
    slot.map.setOptions(mapOptions);
  }

  scheduleReset(slot, resetView);
  return slot.map;
}

export function releasePooledMap(slotId, ownerId, cleanup) {
  let slot = ensureSlot(slotId);

  if (slot.ownerId !== ownerId) {
    return;
  }

  let cleanupFn = typeof cleanup === "function" ? cleanup : slot.cleanup;

  if (typeof cleanupFn === "function") {
    cleanupFn(slot.map);
  }

  if (slot.map) {
    clearMapControls(slot.map);
    google.maps.event.clearInstanceListeners(slot.map);
  }

  clearScheduledResets(slot);

  let shouldRecreateOnRelease = slot.recreateOnRelease === true;
  let container = ensureContainer(slot);

  if (shouldRecreateOnRelease) {
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
    container.textContent = "";
    slot.container = null;
    slot.map = null;
  } else {
    ensureParkingNode().appendChild(container);
  }

  slot.ownerId = null;
  slot.mountNode = null;
  slot.cleanup = null;
  slot.recreateOnRelease = false;
}

export function acquireMainMap(ownerId, mountNode, config = {}) {
  return acquirePooledMap(MAIN_MAP_SLOT, ownerId, mountNode, config);
}

export function releaseMainMap(ownerId, cleanup) {
  releasePooledMap(MAIN_MAP_SLOT, ownerId, cleanup);
}

export function cancelPooledMapReset(slotId, ownerId) {
  let slot = ensureSlot(slotId);

  if (slot.ownerId !== ownerId) {
    return;
  }

  clearScheduledResets(slot);
}

export function cancelMainMapReset(ownerId) {
  cancelPooledMapReset(MAIN_MAP_SLOT, ownerId);
}

export function isPooledMapOwner(slotId, ownerId) {
  return ensureSlot(slotId).ownerId === ownerId;
}

export function isMainMapOwner(ownerId) {
  return isPooledMapOwner(MAIN_MAP_SLOT, ownerId);
}